import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import OrderService from "../../../services/order/order-service";
import { ApplicationOrderList } from "../../../models/order/application-order-list";
import { ApplicationError } from "../../../models/errors/application-error";
import { ApplicationOrder } from "../../../models/order/application-order";
import { showSuccess } from "../../../components/notification/toastr-actions";
import { resetBasket } from "./basket/basket-slice";
import { ApplicationOrderLineList } from "../../../models/order/application-order-line-list";
import { ApplicationOrderLineUpdate } from "../../../models/order/application-order-line-update";
import { ApplicationOrderStatus } from "../../../models/order/application-order-status";
import { ApplicationOrderSummary } from "../../../models/order/application-order-summary";
import { FetchParams } from "../../../components/controls/table-view";
export const fetchActiveOrders = createAsyncThunk<
  ApplicationOrderList | null,
  { branchId: number, fetchParams: FetchParams },
  { state: RootState }
>("order/activeOrders", async ({ branchId, fetchParams }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const orderService = new OrderService();
  try {
    var currentPosition: number =
      state.order.activeOrders?.currentPosition ?? 0;
    var fetchNext: number = 15;
    var count: number = state.order.activeOrders?.count ?? -1;

    if (fetchParams.reset) {
      currentPosition = 0;
      count = -1;
      dispatch(resetActiveOrders())
    }
    if (currentPosition != count) {
      return await orderService.get(
        branchId,
        currentPosition,
        fetchNext,
        fetchParams.searchText ?? "",
        ApplicationOrderStatus.Active);
    }
    return null;
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});
export const fetchDeliveredOrders = createAsyncThunk<
  ApplicationOrderList | null,
  { branchId: number, fetchParams: FetchParams },
  { state: RootState }
>("order/deliveredOrders", async ({ branchId, fetchParams }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const orderService = new OrderService();
  try {
    var currentPosition: number =
      state.order.deliveredOrders?.currentPosition ?? 0;
    var fetchNext: number = 15;
    var count: number = state.order.deliveredOrders?.count ?? -1;

    if (fetchParams.reset) {
      currentPosition = 0;
      count = -1;
      dispatch(resetDeliveredOrders())
    }
    if (currentPosition != count) {
      return await orderService.get(
        branchId,
        currentPosition,
        fetchNext,
        fetchParams.searchText ?? "",
        ApplicationOrderStatus.Delivered
      );
    }
    return null;
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const fetchPendingOrders = createAsyncThunk<
  ApplicationOrderList | null,
  { branchId: number, fetchParams: FetchParams },
  { state: RootState }
>("order/pendingOrders", async ({ branchId, fetchParams }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const orderService = new OrderService();
  try {
    var currentPosition: number =
      state.order.pendingOrders?.currentPosition ?? 0;
    var fetchNext: number = 15;
    var count: number = state.order.pendingOrders?.count ?? -1;

    if (fetchParams.reset) {
      currentPosition = 0;
      count = -1;
      dispatch(resetPendingOrders())
    }
    if (currentPosition != count) {
      return await orderService.get(
        branchId,
        currentPosition,
        fetchNext,
        fetchParams.searchText ?? "",
        ApplicationOrderStatus.Pending
      );
    }
    return null;
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const createOrder = createAsyncThunk<
  ApplicationOrder[],
  number,
  { state: RootState }
>("order/create", async (basketId, { dispatch, rejectWithValue }) => {
  const orderService = new OrderService();
  try {
    var orders = await orderService.create(basketId);
    dispatch(showSuccess(`Order ${orders.map(x => x.id).join(", ")} Created`));
    dispatch(resetBasket());
    return orders;
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const validateBasket = createAsyncThunk<
  string[],
  number,
  { state: RootState }
>("order/validateBasket", async (basketId, { rejectWithValue }) => {
  const orderService = new OrderService();
  try {
    return await orderService.validateBasket(basketId);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const fetchOrderLines = createAsyncThunk<
  ApplicationOrderLineList | null,
  { orderId: number, fetchParams: FetchParams },
  { state: RootState }
>("viewOrder/Lines", async ({ orderId, fetchParams }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const orderService = new OrderService();
  try {
    var currentPosition: number = state.order.orderLines?.currentPosition ?? 0;
    var fetchNext: number = 15;
    var count: number = state.order.orderLines?.count ?? -1;
    if (fetchParams.reset) {
      currentPosition = 0;
      count = -1;
      dispatch(resetOrderLines())
    }
    if (currentPosition != count) {
      return await orderService.getOrderLines(
        orderId,
        currentPosition,
        fetchNext,
        fetchParams.searchText ?? ""
      );
    }
    return null;
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const fetchOrder = createAsyncThunk<
  ApplicationOrder,
  number,
  { state: RootState }
>("viewOrder/order", async (orderId, { getState, rejectWithValue }) => {
  const orderService = new OrderService();
  try {
    return await orderService.getById(orderId);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});
export const generatePDF = createAsyncThunk<Blob, number, { state: RootState }>(
  "pdf/generate",
  async (orderId, { rejectWithValue }) => {
    const pdfService = new OrderService();
    try {
      const pdfBlob = await pdfService.getOrderPDF(orderId);
      return pdfBlob;
    } catch (error: any) {
      const apiError = ApplicationError.handleApiError(error, {});
      return rejectWithValue(apiError);
    }
  }
);
export const fetchOrderSummary = createAsyncThunk<
  ApplicationOrderSummary,
  number,
  { state: RootState }
>("order/summary", async (orderId, { rejectWithValue }) => {
  const orderService = new OrderService();
  try {
    return await orderService.getOrderSummary(orderId);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});
export const updateOrderline = createAsyncThunk<
  ApplicationOrderLineList | null,
  { orderId: number; orders: ApplicationOrderLineUpdate[], completeOrder: boolean },
  { state: RootState }
>("order/updateSupplier", async ({ orderId, orders, completeOrder }, { rejectWithValue, dispatch }) => {
  const orderService = new OrderService();
  try {
    const response = await orderService.updateOrder(orderId, orders, completeOrder);
    dispatch(fetchOrder(orderId));

    return response;
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

interface OrderState {
  activeOrders: ApplicationOrderList | null;
  deliveredOrders: ApplicationOrderList | null;
  pendingOrders: ApplicationOrderList | null;
  loading: boolean;
  deliveryMode: boolean;
  order: ApplicationOrder | null;
  orderLines: ApplicationOrderLineList | null;
  orderSummary: ApplicationOrderSummary | null;
  orderSummaryLoading: boolean;
  orderSummaryError: string | null;
  errors: string[];
}
const initialState: OrderState = {
  activeOrders: null,
  deliveredOrders: null,
  pendingOrders: null,
  loading: false,
  deliveryMode: false,
  order: null,
  orderLines: null,
  orderSummary: null,
  orderSummaryLoading: false,
  orderSummaryError: null,
  errors: [],
};

const createOrderSlice = createSlice({
  name: "createOrder",
  initialState,
  reducers: {
    resetActiveOrders: (state) => {
      state.activeOrders = initialState.activeOrders;
    },
    resetDeliveredOrders: (state) => {
      state.deliveredOrders = initialState.deliveredOrders;
    },
    resetPendingOrders: (state) => {
      state.pendingOrders = initialState.pendingOrders;
    },
    resetOrder: (state) => {
      state.order = initialState.order;
      state.orderLines = initialState.orderLines;
      state.orderSummary = initialState.orderSummary;
      state.orderSummaryLoading = initialState.orderSummaryLoading;
      state.orderSummaryError = initialState.orderSummaryError;
    },
    resetOrderLines: (state) => {
      state.orderLines = initialState.orderLines;
    },
    resetOrderSummary: (state) => {
      state.orderSummary = initialState.orderSummary;
      state.orderSummaryLoading = initialState.orderSummaryLoading;
      state.orderSummaryError = initialState.orderSummaryError;
    },
    toggleDeliveryMode: (state) => {
      state.deliveryMode = !state.deliveryMode;
    },
    setNewValue: (
      state,
      action: PayloadAction<{
        lineId: number;
        parentLineId?: number;
        field: "quantityReceived" | "deliveryNotes";
        value: number | string;
      }>
    ) => {
      const { lineId, parentLineId, field, value } = action.payload;

      if (parentLineId != null) {
        const parentLine = state.orderLines?.orderLines.find(
          (line) => line.id === parentLineId
        );
        const splitLine = parentLine?.children?.find((child) => child.id === lineId);

        if (splitLine) {
          (splitLine as any)[field] = value;
          return;
        }
      }

      const orderLine = state.orderLines?.orderLines.find((line) => line.id === lineId);
      if (orderLine) {
        (orderLine as any)[field] = value;
      }
    },
    resetChanges: (state) => {
      if (state.orderLines) {
        state.orderLines.orderLines = state.orderLines.orderLines.map(
          (line) => {
            const { quantityReceived, deliveryNotes } =
              extractOrderLineDetails(line);
            return {
              ...line,
              quantityReceived,
              deliveryNotes,
              children: (line.children ?? []).map((child) => {
                const {
                  quantityReceived: childQuantityReceived,
                  deliveryNotes: childDeliveryNotes,
                } = extractOrderLineDetails(child);
                return {
                  ...child,
                  quantityReceived: childQuantityReceived,
                  deliveryNotes: childDeliveryNotes,
                };
              }),
            };
          }
        );
      }
      state.deliveryMode = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchActiveOrders.fulfilled,
        (state, action: PayloadAction<ApplicationOrderList | null>) => {
          state.deliveryMode = false;
          if (action.payload) {
            if (state.activeOrders?.orders) {
              state.activeOrders.orders = state.activeOrders.orders.concat(
                action.payload!.orders
              );
              state.activeOrders.currentPosition =
                action.payload!.currentPosition;
              state.activeOrders.count = action.payload!.count;
            } else {
              state.activeOrders = action.payload;
            }
          }
        }
      )
      .addCase(
        fetchDeliveredOrders.fulfilled,
        (state, action: PayloadAction<ApplicationOrderList | null>) => {
          if (action.payload) {
            if (state.deliveredOrders?.orders) {
              state.deliveredOrders.orders =
                state.deliveredOrders.orders.concat(action.payload!.orders);
              state.deliveredOrders.currentPosition =
                action.payload!.currentPosition;
              state.deliveredOrders.count = action.payload!.count;
            } else {
              state.deliveredOrders = action.payload;
            }
          }
        }
      )
      .addCase(
        fetchPendingOrders.fulfilled,
        (state, action: PayloadAction<ApplicationOrderList | null>) => {
          if (action.payload) {
            if (state.pendingOrders?.orders) {
              state.pendingOrders.orders =
                state.pendingOrders.orders.concat(action.payload!.orders);
              state.pendingOrders.currentPosition =
                action.payload!.currentPosition;
              state.pendingOrders.count = action.payload!.count;
            } else {
              state.pendingOrders = action.payload;
            }
          }
        }
      )
      .addCase(createOrder.pending, handlePending)
      .addCase(createOrder.rejected, handleError)
      .addCase(validateBasket.pending, (state) => {
        state.errors = [];
      })
      .addCase(
        validateBasket.fulfilled,
        (state, action: PayloadAction<string[]>) => {
          state.errors = action.payload ?? [];
        }
      )
      .addCase(validateBasket.rejected, (state, action) => {
        const payload: any = action.payload;
        state.errors = [payload?.message ?? "Failed to validate basket"];
      })
      .addCase(
        createOrder.fulfilled,
        (state, action: PayloadAction<ApplicationOrder[]>) => {
          if (state.pendingOrders?.orders) {
            state.pendingOrders.count += action.payload.length;
            state.pendingOrders.orders = [
              ...action.payload,
              ...state.pendingOrders.orders
            ];
            state.pendingOrders.currentPosition += action.payload.length;
          } else {
            state.pendingOrders = {
              orders: action.payload,
              count: action.payload.length,
              currentPosition: action.payload.length,
            };
          }

          state.loading = false;
        }
      )
      .addCase(
        fetchOrderLines.fulfilled,
        (state, action) => {
          const isResetFetch = action.meta.arg.fetchParams.reset === true;
          if (action.payload) {
            if (state.orderLines?.orderLines) {
              state.orderLines.orderLines =
                state.orderLines.orderLines.concat(action.payload.orderLines);
              state.orderLines.currentPosition = action.payload.currentPosition;
              state.orderLines.count = action.payload.count;
            } else {
              state.orderLines = {
                orderLines: action.payload.orderLines,
                count: action.payload.count,
                currentPosition: action.payload.currentPosition,
              };
            }
            if (isResetFetch) {
              state.deliveryMode = false;
            }
          }
        }
      )
      .addCase(
        fetchOrder.fulfilled,
        (state, action: PayloadAction<ApplicationOrder>) => {
          const order = action.payload;
          state.order = order;

          const { Active, Delivered } = ApplicationOrderStatus;

          const removeFromOrders = (orders: ApplicationOrder[], id: number): ApplicationOrder[] => {
            return orders?.filter(x => x.id !== id) ?? [];
          };

          const addToOrdersIfNotExists = (orders: ApplicationOrder[], order: ApplicationOrder): void => {
            if (!orders.some(x => x.id === order.id)) {
              orders.unshift(order);
            }
          };

          if (order.status === Active) {
            if (state.deliveredOrders) {
              state.deliveredOrders.orders = removeFromOrders(state.deliveredOrders.orders, order.id);
            }

            if (state.activeOrders) {
              addToOrdersIfNotExists(state.activeOrders.orders, order);
            }
          }

          if (order.status === Delivered) {
            if (state.activeOrders) {
              state.activeOrders.orders = removeFromOrders(state.activeOrders.orders, order.id);
            }

            if (state.deliveredOrders) {
              addToOrdersIfNotExists(state.deliveredOrders.orders, order);
            }
          }
        }
      )
      .addCase(fetchOrderSummary.pending, (state) => {
        state.orderSummaryLoading = true;
        state.orderSummaryError = null;
      })
      .addCase(
        fetchOrderSummary.fulfilled,
        (state, action: PayloadAction<ApplicationOrderSummary>) => {
          state.orderSummary = action.payload;
          state.orderSummaryLoading = false;
          state.orderSummaryError = null;
        }
      )
      .addCase(fetchOrderSummary.rejected, (state, action) => {
        const payload: any = action.payload;
        state.orderSummaryLoading = false;
        state.orderSummaryError = payload?.message ?? "Failed to fetch order summary";
      })
      .addCase(
        updateOrderline.fulfilled,
        (state, action: PayloadAction<ApplicationOrderLineList | null>) => {
          if (action.payload) {  
            if (state.orderLines?.orderLines) {
              state.orderLines.orderLines = action.payload.orderLines;
              state.orderLines.count = action.payload.count;
              state.orderLines.currentPosition = action.payload.currentPosition;
            }
            state.deliveryMode = false;
          }
        }
      );
  },
});

const handlePending = (state: OrderState) => {
  state.loading = true;
};

const handleError = (state: OrderState) => {
  state.loading = false;
};
const extractOrderLineDetails = (line: any) => {
  const notes = line.notes ?? "";
  const quantityReceived = notes.match(/(\d+) units received/)?.[1] || "0";
  const deliveryNotes = notes.match(/Delivery notes: (.+)/)?.[1] || "";

  return {
    quantityReceived,
    deliveryNotes,
  };
};
export const {
  resetOrder,
  resetActiveOrders,
  toggleDeliveryMode,
  setNewValue,
  resetChanges,
  resetDeliveredOrders,
  resetOrderLines,
  resetPendingOrders,
  resetOrderSummary
} = createOrderSlice.actions;
export default createOrderSlice.reducer;
